import { makeAutoObservable, runInAction } from 'mobx';
import { captureMessage } from '@sentry/nextjs';
import { classifyWalletReadFailure, type WalletReadFailure } from '@/shared/utils/walletReadFailure';

import type {
  ApiPaymentService,
  StoreCreditBalanceResponse,
  StoreCreditHistoryResponse,
  WalletCredit,
  WalletEligibleCategory,
  WalletHistoryCredit,
} from '@/core/infrastructure/services/ApiPaymentService';

export type ShoppingWalletActivityKind = 'credit' | 'usage';
export type ShoppingWalletActivityStatus = 'active' | 'used' | 'expired' | 'cancelled';

export interface ShoppingWalletActivityItem {
  id: string;
  kind: ShoppingWalletActivityKind;
  title: string;
  subtitle: string;
  amount: string;
  currency: string;
  direction: 'in' | 'out';
  status: ShoppingWalletActivityStatus;
  timestamp: string;
  expiresAt?: string | null;
  minimumPurchaseAmount?: string | null;
  redemptionScope?: string;
  eligiblePurchaseStartedAt?: string | null;
  eligibleProductCategoryIds?: string[];
  eligibleProductCategories?: WalletEligibleCategory[];
}

export interface ShoppingWalletAnnouncementSummary {
  amount: string;
  currency: string;
  expiresAt?: string | null;
  creditIds: string[];
}

function statusFromRaw(value?: string): ShoppingWalletActivityStatus {
  const normalized = String(value || '').trim().toUpperCase();
  if (normalized === 'USED') return 'used';
  if (normalized === 'EXPIRED') return 'expired';
  if (normalized === 'CANCELLED') return 'cancelled';
  return 'active';
}

function titleFromCredit(credit: Pick<WalletCredit, 'description' | 'reason'>): string {
  switch (String(credit.reason || '').toUpperCase()) {
    case 'PROMOTIONAL':
    case 'GOODWILL':
      return 'Gift';
    case 'REFUND':
      return 'Refund';
    case 'PRICE_DIFFERENCE':
      return 'Price difference';
    case 'KMP_PRICE_DROP':
      return 'Keep My Price';
    case 'COMPENSATION':
      return 'Compensation';
    default:
      return credit.description || 'Shopping Wallet credit';
  }
}

function isPromotionalCredit(credit: Pick<WalletCredit, 'reason' | 'remaining_amount'>): boolean {
  return (
    String(credit.reason || '').toUpperCase() === 'PROMOTIONAL' &&
    Number(credit.remaining_amount || 0) > 0
  );
}

function compareExpiryAscending(left?: string | null, right?: string | null): number {
  const leftTime = left ? new Date(left).getTime() : Number.POSITIVE_INFINITY;
  const rightTime = right ? new Date(right).getTime() : Number.POSITIVE_INFINITY;
  return leftTime - rightTime;
}

function creditSubtitle(credit: Pick<WalletHistoryCredit, 'issued_at' | 'created_at'>): string {
  return String(credit.issued_at || credit.created_at || '').trim();
}

function creditRestrictions(credit: WalletCredit) {
  return {
    minimumPurchaseAmount: credit.minimum_purchase_amount,
    redemptionScope: credit.redemption_scope,
    eligiblePurchaseStartedAt: credit.eligible_purchase_started_at,
    eligibleProductCategoryIds: credit.eligible_product_category_ids,
    eligibleProductCategories: credit.eligible_product_categories,
  };
}

function fallbackActivityFromBalance(balance: StoreCreditBalanceResponse): ShoppingWalletActivityItem[] {
  return balance.credits.map((credit) => ({
    id: `credit:${credit.id}`,
    kind: 'credit',
    title: titleFromCredit(credit),
    subtitle: String(credit.issued_at || credit.created_at || '').trim(),
    amount: credit.amount,
    currency: credit.currency,
    direction: 'in',
    status: statusFromRaw(credit.status),
    timestamp: String(credit.issued_at || credit.created_at || '').trim(),
    expiresAt: credit.expires_at,
    ...creditRestrictions(credit),
  }));
}

function buildActivityItems(historyCredits: WalletHistoryCredit[]): ShoppingWalletActivityItem[] {
  const items: ShoppingWalletActivityItem[] = [];

  for (const credit of historyCredits) {
    items.push({
      id: `credit:${credit.id}`,
      kind: 'credit',
      title: titleFromCredit(credit),
      subtitle: creditSubtitle(credit),
      amount: credit.amount,
      currency: credit.currency,
      direction: 'in',
      status: statusFromRaw(credit.status),
      timestamp: String(credit.issued_at || credit.created_at || '').trim(),
      expiresAt: credit.expires_at,
      ...creditRestrictions(credit),
    });

    for (const usage of credit.usage_history) {
      items.push({
        id: `usage:${credit.id}:${usage.application_id}`,
        kind: 'usage',
        title: usage.order_id ? 'Applied to your order' : 'Applied at checkout',
        subtitle: usage.applied_at,
        amount: usage.amount_used,
        currency: credit.currency,
        direction: 'out',
        status: 'used',
        timestamp: usage.applied_at,
      });
    }
  }

  return items.sort((left, right) => {
    const leftTime = left.timestamp ? new Date(left.timestamp).getTime() : 0;
    const rightTime = right.timestamp ? new Date(right.timestamp).getTime() : 0;
    return rightTime - leftTime;
  });
}

export class ShoppingWalletStore {
  balance = 0;
  currency = 'GHS';
  balanceSnapshot: StoreCreditBalanceResponse | null = null;
  historySnapshot: StoreCreditHistoryResponse | null = null;
  isLoading = false;
  isHistoryLoading = false;
  balanceError: string | null = null;
  historyError: string | null = null;
  balanceFailure: WalletReadFailure | null = null;
  historyFailure: WalletReadFailure | null = null;
  hasLoaded = false;
  hasLoadedHistory = false;
  private loadRequestToken = 0;
  private balanceReloadRequested = false;
  private historyReloadRequested = false;

  constructor(private readonly paymentService: ApiPaymentService) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get error(): string | null {
    return this.balanceError || this.historyError;
  }

  get isRefreshing(): boolean {
    return this.isLoading || this.isHistoryLoading;
  }

  get canRetry(): boolean {
    return Boolean(
      (this.balanceError && !this.isLoading) ||
      (this.historyError && !this.isHistoryLoading)
    );
  }

  get activeCredits(): WalletCredit[] {
    return this.balanceSnapshot?.credits ?? [];
  }

  get promotionalCredits(): WalletCredit[] {
    return this.activeCredits.filter(isPromotionalCredit);
  }

  getUntouchedGiftCredits(nowMs: number): WalletCredit[] {
    return this.activeCredits.filter((credit) => {
      const reason = String(credit.reason || '').toUpperCase();
      const amount = Number(credit.amount);
      const remaining = Number(credit.remaining_amount);
      // A checkout allocation that was removed restores the gift's full value.
      // Display eligibility follows available value, never changes redemption rules.
      return (reason === 'PROMOTIONAL' || reason === 'GOODWILL') &&
        credit.status === 'ACTIVE' && Number.isFinite(amount) && amount > 0 &&
        remaining === amount &&
        (credit.expires_at === null || Date.parse(credit.expires_at) > nowMs);
    });
  }

  async refreshBalance(): Promise<void> {
    if (this.isLoading) {
      this.balanceReloadRequested = true;
      return;
    }
    await this.loadBalanceCycle(this.loadRequestToken);
  }

  get historyCredits(): WalletHistoryCredit[] {
    return this.historySnapshot?.credits ?? [];
  }

  get hasWalletHistory(): boolean {
    return this.historyCredits.length > 0;
  }

  get hasSpendableBalance(): boolean {
    return this.balance > 0;
  }

  get isWalletRelevant(): boolean {
    return this.hasSpendableBalance || this.activeCredits.length > 0 || this.hasWalletHistory;
  }

  get promotionalAnnouncement(): ShoppingWalletAnnouncementSummary | null {
    if (!this.promotionalCredits.length) {
      return null;
    }

    const expiringSoonId = this.balanceSnapshot?.expiring_soon?.credit_id;
    const expiringSoonCredit = expiringSoonId
      ? this.promotionalCredits.find((credit) => credit.id === expiringSoonId)
      : null;

    const expirySet = new Set(
      this.promotionalCredits
        .map((credit) => String(credit.expires_at || '').trim())
        .filter(Boolean)
    );
    const hasUniformExpiry = expirySet.size <= 1;
    const earliestExpiringCredit = [...this.promotionalCredits].sort((left, right) =>
      compareExpiryAscending(left.expires_at, right.expires_at)
    )[0];
    const announcedCredits = hasUniformExpiry
      ? this.promotionalCredits
      : expiringSoonCredit
        ? [expiringSoonCredit]
        : earliestExpiringCredit
          ? [earliestExpiringCredit]
          : this.promotionalCredits;
    const amount = announcedCredits
      .reduce((sum, credit) => sum + (Number(credit.remaining_amount || credit.amount) || 0), 0)
      .toFixed(2);
    const expiresAt = announcedCredits
      .map((credit) => credit.expires_at)
      .filter(Boolean)
      .sort(compareExpiryAscending)[0];

    return {
      amount,
      currency: this.balanceSnapshot?.currency || this.promotionalCredits[0]?.currency || this.currency,
      expiresAt,
      creditIds: this.promotionalCredits
        .map((credit) => credit.id)
        .filter(Boolean)
        .sort(),
    };
  }

  get activityItems(): ShoppingWalletActivityItem[] {
    if (this.historyCredits.length > 0) {
      return buildActivityItems(this.historyCredits);
    }
    if (this.balanceSnapshot) {
      return fallbackActivityFromBalance(this.balanceSnapshot);
    }
    return [];
  }

  private async loadHistoryPages(requestToken: number): Promise<StoreCreditHistoryResponse> {
    const visitedPages = new Set<string>();
    const credits: WalletHistoryCredit[] = [];
    let nextPageUrl: string | undefined;
    let latestPagination: StoreCreditHistoryResponse['pagination'] = null;

    while (true) {
      const page = await this.paymentService.getStoreCreditHistory(nextPageUrl);
      if (requestToken !== this.loadRequestToken) {
        return { credits: [], pagination: null };
      }
      credits.push(...page.credits);
      latestPagination = page.pagination ?? null;

      const next = latestPagination?.next || undefined;
      if (!next || visitedPages.has(next)) {
        return {
          credits,
          pagination: latestPagination,
        };
      }

      visitedPages.add(next);
      nextPageUrl = next;
    }
  }

  async loadWallet(force = false): Promise<void> {
    const requestToken = this.loadRequestToken;
    const loads: Promise<void>[] = [];

    if (this.isLoading) {
      if (force) this.balanceReloadRequested = true;
    } else if (!this.hasLoaded || this.balanceError || force) {
      loads.push(this.loadBalanceCycle(requestToken));
    }

    if (this.isHistoryLoading) {
      if (force) this.historyReloadRequested = true;
    } else if (!this.hasLoadedHistory || this.historyError || force) {
      loads.push(this.loadHistoryCycle(requestToken));
    }

    await Promise.all(loads);
  }

  private async loadBalanceCycle(requestToken: number): Promise<void> {
    if (requestToken !== this.loadRequestToken) return;
    runInAction(() => {
      this.isLoading = true;
    });

    await this.loadBalance(requestToken);
    if (requestToken !== this.loadRequestToken) return;

    let reloadRequested = false;
    runInAction(() => {
      reloadRequested = this.balanceReloadRequested;
      this.balanceReloadRequested = false;
    });
    if (reloadRequested) {
      await this.loadBalanceCycle(requestToken);
    } else {
      runInAction(() => { this.isLoading = false; });
    }
  }

  private async loadHistoryCycle(requestToken: number): Promise<void> {
    if (requestToken !== this.loadRequestToken) return;
    runInAction(() => {
      this.isHistoryLoading = true;
      this.historyError = null;
    });

    await this.loadHistory(requestToken);
    if (requestToken !== this.loadRequestToken) return;

    let reloadRequested = false;
    runInAction(() => {
      reloadRequested = this.historyReloadRequested;
      this.historyReloadRequested = false;
    });
    if (reloadRequested) {
      await this.loadHistoryCycle(requestToken);
    } else {
      runInAction(() => { this.isHistoryLoading = false; });
    }
  }

  private async loadBalance(requestToken: number): Promise<void> {
    try {
      const balance = await this.paymentService.getStoreCreditBalance();

      runInAction(() => {
        if (requestToken !== this.loadRequestToken) {
          return;
        }

        this.balanceSnapshot = balance;
        this.balance = Number(balance.balance) || 0;
        this.currency = balance.currency || 'GHS';
        this.hasLoaded = true;
        this.balanceError = null;
        this.balanceFailure = null;
      });
    } catch (error) {
      runInAction(() => {
        if (requestToken !== this.loadRequestToken) {
          return;
        }

        if (!this.hasLoaded) {
          this.balanceSnapshot = null;
          this.balance = 0;
          this.currency = 'GHS';
        }
        const firstFailure = this.balanceError === null;
        this.balanceError = 'Failed to load Shopping Wallet balance';
        this.balanceFailure = classifyWalletReadFailure(error);
        if (firstFailure) {
          captureMessage('Shopping Wallet balance refresh unavailable', {
            level: 'warning', extra: { wallet_read: this.balanceFailure },
          });
        }
      });
    }
  }

  private async loadHistory(requestToken: number): Promise<void> {
    try {
      const history = await this.loadHistoryPages(requestToken);

      runInAction(() => {
        if (requestToken !== this.loadRequestToken) {
          return;
        }

        this.historySnapshot = history;
        this.hasLoadedHistory = true;
        this.historyError = null;
        this.historyFailure = null;
      });
    } catch (error) {
      runInAction(() => {
        if (requestToken !== this.loadRequestToken) {
          return;
        }

        this.historyError = 'Activity could not be refreshed';
        this.historyFailure = classifyWalletReadFailure(error);
      });
    }
  }

  clear(): void {
    this.loadRequestToken += 1;
    this.balanceReloadRequested = false;
    this.historyReloadRequested = false;
    this.balance = 0;
    this.currency = 'GHS';
    this.balanceSnapshot = null;
    this.historySnapshot = null;
    this.isLoading = false;
    this.isHistoryLoading = false;
    this.balanceError = null;
    this.historyError = null;
    this.balanceFailure = null;
    this.historyFailure = null;
    this.hasLoaded = false;
    this.hasLoadedHistory = false;
  }
}
